import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/shared/hooks/use-toast';
import { useSubscription } from '../useSubscription';

interface SubscriptionGuardProps {
  children: React.ReactNode;
  fallbackPath?: string;
}

/**
 * Route-level guard that redirects to settings if user has no active subscription.
 * Modeled after OnboardingGuard.
 */
export function SubscriptionGuard({
  children,
  fallbackPath = '/app/settings',
}: SubscriptionGuardProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { isActive, isLoading } = useSubscription();

  useEffect(() => {
    if (!isLoading && !isActive) {
      toast({
        title: t('billing.invoice.guardTitle'),
        description: t('billing.invoice.guardDescription'),
        variant: 'destructive',
      });
      navigate(fallbackPath, { replace: true });
    }
  }, [isLoading, isActive, navigate, fallbackPath, toast, t]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isActive) return null;

  return <>{children}</>;
}
